// =============================================================================
// documents.ts — Schemas Zod públicos do catálogo de tipos de documento (F46-S02).
//
// Contexto: docs/planejamento-documentos-anexos.md §3.1 (normativo).
//
// Cobre apenas `document_types` (o catálogo administrável). Os schemas de
// `documents` (upload/download/revisão do documento em si) nascem na F46-S04 —
// fora de escopo aqui.
//
// LGPD: document_types não contém PII — é metadado de configuração
// (chave/rótulo/categoria do TIPO de documento, não o documento em si).
// =============================================================================
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Domínios fechados
// ---------------------------------------------------------------------------
/**
 * Categoria fechada do tipo de documento — espelha o CHECK
 * `chk_document_types_category` da migration 0138.
 */
export const DocumentTypeCategorySchema = z.enum([
    'identificacao',
    'renda',
    'endereco',
    'negocio',
    'garantia',
    'contrato',
    'juridico',
    'outros',
]);
/**
 * Fluxos em que um tipo de documento pode ser exigido. Fonte do checklist de
 * pendências do lead (F46-S06, fora de escopo aqui).
 */
export const DocumentTypeRequiredForSchema = z.enum(['credit_analysis', 'contract']);
/** MIME types aceitos por padrão — mesmo default da coluna `accepted_mimes`. */
export const DEFAULT_DOCUMENT_ACCEPTED_MIMES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
];
// ---------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------
export const DocumentTypeSchema = z.object({
    id: z.string().uuid(),
    organization_id: z.string().uuid(),
    key: z.string().describe('Chave estável do tipo (ex.: "rg", "comprovante_residencia")'),
    label: z.string().describe('Rótulo exibido ao usuário (ex.: "Comprovante de residência")'),
    category: DocumentTypeCategorySchema,
    required_for: z.array(DocumentTypeRequiredForSchema),
    expires_after_days: z
        .number()
        .int()
        .positive()
        .nullable()
        .describe('Dias de validade a partir da aprovação. null = não vence.'),
    accepted_mimes: z.array(z.string()),
    max_size_bytes: z.number().int().positive(),
    active: z.boolean(),
    sort_order: z.number().int(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
});
export const DocumentTypeListResponseSchema = z.object({
    data: z.array(DocumentTypeSchema),
});
// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------
export const DocumentTypeCreateSchema = z.object({
    key: z
        .string({ required_error: 'key é obrigatório' })
        .min(1)
        .max(100)
        .regex(/^[a-z][a-z0-9_]*$/, 'key deve ser snake_case (ex.: "comprovante_residencia")')
        .describe('Chave estável, única por organização — usada por código/UI'),
    label: z.string({ required_error: 'label é obrigatório' }).min(1).max(255),
    category: DocumentTypeCategorySchema,
    required_for: z.array(DocumentTypeRequiredForSchema).default([]),
    expires_after_days: z.number().int().positive().nullable().default(null),
    accepted_mimes: z
        .array(z.string().min(1))
        .min(1)
        .default([...DEFAULT_DOCUMENT_ACCEPTED_MIMES]),
    max_size_bytes: z.number().int().positive().default(10_485_760),
    active: z.boolean().default(true),
    sort_order: z.number().int().default(0),
});
// ---------------------------------------------------------------------------
// Update (partial — todos os campos opcionais, exceto `key` que é imutável)
// ---------------------------------------------------------------------------
export const DocumentTypeUpdateSchema = z.object({
    label: z.string().min(1).max(255).optional(),
    category: DocumentTypeCategorySchema.optional(),
    required_for: z.array(DocumentTypeRequiredForSchema).optional(),
    expires_after_days: z.number().int().positive().nullable().optional(),
    accepted_mimes: z.array(z.string().min(1)).min(1).optional(),
    max_size_bytes: z.number().int().positive().optional(),
    active: z.boolean().optional(),
    sort_order: z.number().int().optional(),
});
// ---------------------------------------------------------------------------
// List query — inclui inativos por padrão (tela de admin precisa ver tudo)
// ---------------------------------------------------------------------------
export const DocumentTypeListQuerySchema = z.object({
    include_inactive: z
        .enum(['true', 'false'])
        .default('true')
        .transform((v) => v === 'true'),
});
// =============================================================================
// documents (F46-S04) — upload/download/revisão do documento em si.
//
// Contexto: docs/planejamento-documentos-anexos.md §3.2/§4 (normativo).
//
// LGPD (doc 17 §8.3/§8.5):
//   - `original_filename` PODE conter PII (ex.: "CPF_JOAO_SILVA.pdf") — este
//     schema é o contrato de RESPOSTA HTTP autenticada e autorizada (RBAC +
//     escopo de cidade já aplicados antes de chegar aqui), não um evento nem
//     um log. Nunca reusar estes campos em `event_outbox.payload` ou em log.
//   - `storage_key` é OPACA mas deliberadamente NÃO exposta aqui — a
//     superfície pública não precisa dela (download é por `document.id`),
//     e omiti-la reduz a superfície de exposição.
//   - Nenhum campo de URL assinada nasce aqui — a URL de download é um 302,
//     nunca um campo JSON persistido ou reexibido.
// =============================================================================
export const DocumentSourceSchema = z.enum(['upload_ui', 'whatsapp_inbound', 'import', 'api']);
export const DocumentScanStatusSchema = z.enum(['pending', 'clean', 'rejected']);
export const DocumentStatusSchema = z.enum(['pending_review', 'approved', 'rejected', 'expired']);
// ---------------------------------------------------------------------------
// POST /api/documents/signed-url
// ---------------------------------------------------------------------------
export const SignedUrlRequestSchema = z
    .object({
    leadId: z.string().uuid().describe('Lead ao qual o documento será anexado'),
    documentTypeId: z
        .string()
        .uuid()
        .describe('Tipo do documento no catálogo da organização (document_types.id)'),
    mime: z
        .string()
        .min(1)
        .describe('MIME type declarado do arquivo (validado contra document_types.accepted_mimes)'),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .describe('Tamanho declarado do arquivo em bytes (validado contra document_types.max_size_bytes)'),
})
    .describe('Corpo de POST /api/documents/signed-url');
export const SignedUrlResponseSchema = z
    .object({
    uploadUrl: z
        .string()
        .url()
        .describe('URL assinada PUT para upload direto ao storage privado. TTL 10 min.'),
    key: z
        .string()
        .describe('Chave opaca do objeto, gerada server-side — envie de volta em POST /api/documents.'),
})
    .describe('Resposta de POST /api/documents/signed-url');
// ---------------------------------------------------------------------------
// POST /api/documents
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// POST /api/documents — duas origens mutuamente exclusivas (F46-S23):
//   1. `key` (+ `mime` + `sizeBytes`) — confirma um upload feito via
//      POST /api/documents/signed-url. Fluxo original (F46-S04).
//   2. `messageId` — promove a mídia de uma mensagem do live chat já
//      baixada a documento, SEM re-upload. O cliente NUNCA vê nem envia a
//      key opaca do storage: o backend resolve key/mime/sizeBytes/sha256 a
//      partir da linha em `messages` (doc F46-S23, "o cliente nunca vê a
//      key"). A linha de `documents` aponta para o MESMO objeto da
//      mensagem — nenhuma cópia é criada.
// ---------------------------------------------------------------------------
export const DocumentCreateSchema = z
    .object({
    key: z
        .string()
        .min(1)
        .optional()
        .describe('Key devolvida por POST /api/documents/signed-url. Mutuamente exclusivo com ' +
        '`messageId` — envie exatamente um dos dois.'),
    messageId: z
        .string()
        .uuid()
        .optional()
        .describe('Mensagem do live chat cuja mídia (já baixada) vira documento, sem re-upload. O ' +
        'backend resolve key/mime/sizeBytes/sha256 a partir da mensagem — a key opaca do ' +
        'storage nunca entra nem sai pela borda HTTP. Mutuamente exclusivo com `key`.'),
    leadId: z.string().uuid(),
    documentTypeId: z.string().uuid(),
    mime: z
        .string()
        .min(1)
        .optional()
        .describe('Obrigatório quando `key` é informado. Ignorado (resolvido pelo servidor) quando `messageId` é informado.'),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .optional()
        .describe('Obrigatório quando `key` é informado. Ignorado (resolvido pelo servidor) quando `messageId` é informado.'),
    originalFilename: z
        .string()
        .min(1)
        .max(255)
        .optional()
        .describe('Nome original do arquivo — PII, nunca logado nem propagado a evento'),
    displayName: z
        .string()
        .max(255)
        .optional()
        .describe('Nome de exibição opcional, já na criação (F46-S31) — o agente pode organizar o ' +
        'nome desde o anexo. PII igual a originalFilename; nunca sobrescreve o original. ' +
        'Vazio/só espaço é normalizado para "sem nome de exibição" pelo servidor (o ' +
        'fallback para originalFilename dispara nesse caso).'),
    analysisId: z.string().uuid().optional(),
    contractId: z.string().uuid().optional(),
    paymentDueId: z.string().uuid().optional(),
    supersedesId: z
        .string()
        .uuid()
        .optional()
        .describe('Documento que esta versão substitui (ex.: reenvio após rejeição). Precisa ser do ' +
        'mesmo lead e do mesmo tipo — o anterior é soft-deletado e a versão incrementada, ' +
        'nunca sobrescrita (F46-S06).'),
})
    .describe('Corpo de POST /api/documents')
    .superRefine((data, ctx) => {
    const hasKey = data.key !== undefined;
    const hasMessageId = data.messageId !== undefined;
    if (hasKey === hasMessageId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Envie exatamente um entre "key" (confirmação de upload) e "messageId" (mídia de ' +
                'uma mensagem já existente) — nunca os dois, nem nenhum.',
            path: ['key'],
        });
        return;
    }
    if (hasKey) {
        if (data.mime === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '"mime" é obrigatório quando "key" é informado.',
                path: ['mime'],
            });
        }
        if (data.sizeBytes === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '"sizeBytes" é obrigatório quando "key" é informado.',
                path: ['sizeBytes'],
            });
        }
    }
});
// ---------------------------------------------------------------------------
// Response — documento completo (detalhe, listagem e central do lead)
// ---------------------------------------------------------------------------
export const DocumentSchema = z
    .object({
    id: z.string().uuid(),
    organization_id: z.string().uuid(),
    lead_id: z.string().uuid(),
    customer_id: z.string().uuid().nullable(),
    document_type_id: z.string().uuid(),
    document_type_key: z
        .string()
        .describe('Chave estável do tipo (document_types.key), resolvida para o badge da UI'),
    analysis_id: z.string().uuid().nullable(),
    contract_id: z.string().uuid().nullable(),
    payment_due_id: z.string().uuid().nullable(),
    message_id: z.string().uuid().nullable(),
    mime: z.string(),
    size_bytes: z.number().int(),
    original_filename: z.string().nullable().describe('PII — nome original enviado pelo usuário'),
    display_name: z
        .string()
        .nullable()
        .describe('Nome de exibição editável pelo agente (F46-S31), separado do original — PII igual a ' +
        'original_filename. null = sem organização manual ainda; a UI e o download devem ' +
        'usar `display_name ?? original_filename`.'),
    scan_status: DocumentScanStatusSchema,
    status: DocumentStatusSchema,
    reviewed_by_user_id: z.string().uuid().nullable(),
    reviewed_at: z.string().datetime({ offset: true }).nullable(),
    rejection_reason: z.string().nullable(),
    expires_at: z.string().datetime({ offset: true }).nullable(),
    version: z.number().int(),
    supersedes_id: z.string().uuid().nullable(),
    source: DocumentSourceSchema,
    uploaded_by_user_id: z.string().uuid().nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
})
    .describe('Documento anexado ao lead — detalhe/listagem/central do CRM');
/**
 * Item de listagem — mesmo formato de {@link DocumentSchema}. Mantido como
 * alias nomeado (não apenas `DocumentSchema` reexportado) porque o contrato
 * de listagem e o de detalhe podem divergir no futuro (ex.: listagem ganhar
 * um campo agregado que o detalhe não tem) sem quebrar quem importa
 * `DocumentListItemSchema` hoje.
 */
export const DocumentListItemSchema = DocumentSchema;
export const DocumentListResponseSchema = z.object({
    data: z.array(DocumentListItemSchema),
    pagination: z.object({
        page: z.number().int(),
        limit: z.number().int(),
        total: z.number().int(),
        totalPages: z.number().int(),
    }),
});
// =============================================================================
// PATCH /api/documents/:id/review (F46-S06)
//
// Contexto: docs/planejamento-documentos-anexos.md §4 (normativo).
//
// `rejected` exige `rejectionReason` — o CHECK `chk_documents_rejected_needs_reason`
// já garante isso no banco; este schema devolve 400 legível ANTES de chegar lá.
//
// LGPD: `rejectionReason` é texto livre digitado por um humano — pode conter
// PII do titular (nome, referência a documento). Tratado como PII: nunca em
// log/evento (documents.reviewed carrega apenas o `status`, não o motivo).
// =============================================================================
export const DocumentReviewRequestSchema = z
    .object({
    status: z
        .enum(['approved', 'rejected'])
        .describe('Resultado da conferência humana do documento'),
    rejectionReason: z
        .string()
        .min(1)
        .max(1000)
        .optional()
        .describe('Motivo da rejeição — obrigatório quando status="rejected". Texto livre digitado ' +
        'por um humano; PODE conter PII do titular. Nunca propagado a log/evento.'),
})
    .describe('Corpo de PATCH /api/documents/:id/review');
// =============================================================================
// PATCH /api/documents/:id/display-name (F46-S31)
//
// Contexto: tasks/slots/F46/F46-S31-nome-de-exibicao-do-documento.md.
//
// `display_name` é uma coluna NOVA, separada de `original_filename`
// (proveniência, imutável) — editar o nome NUNCA sobrescreve o original.
// `displayName: null` limpa a organização manual (volta a exibir o
// original). String vazia/só-espaço é normalizada para `null` no servidor —
// não é rejeitada como erro, é tratada como "sem nome de exibição" (senão o
// fallback para o original nunca dispara).
//
// LGPD: display_name é PII igual a original_filename — nunca em log/evento;
// a edição é auditada (quem, quando, valor anterior) no audit log, não no
// outbox.
// =============================================================================
export const DocumentDisplayNameUpdateSchema = z
    .object({
    displayName: z
        .string()
        .max(255)
        .nullable()
        .describe('Novo nome de exibição. null (ou vazio/só espaço, normalizado pelo servidor) limpa a ' +
        'organização manual e volta a exibir original_filename.'),
})
    .describe('Corpo de PATCH /api/documents/:id/display-name');
// =============================================================================
// GET /api/credit-analyses/:id/document-checklist e
// GET /api/contracts/:id/document-checklist (F46-S06)
//
// Contexto: docs/planejamento-documentos-anexos.md §2 (normativo).
//
// Derivado de `document_types.required_for` — nunca hardcoded no código, para
// não divergir quando o catálogo é editado pela tela de admin (F46-S02).
// =============================================================================
export const DocumentChecklistStateSchema = z.enum([
    'missing',
    'pending_review',
    'approved',
    'rejected',
    'expired',
]);
export const DocumentChecklistItemSchema = z
    .object({
    documentTypeId: z.string().uuid(),
    key: z.string().describe('Chave estável do tipo (document_types.key)'),
    label: z.string().describe('Rótulo exibido ao usuário'),
    category: DocumentTypeCategorySchema,
    state: DocumentChecklistStateSchema,
    documentId: z
        .string()
        .uuid()
        .optional()
        .describe('Documento (versão mais recente não deletada) que preenche este item, se houver'),
    expiresAt: z
        .string()
        .datetime({ offset: true })
        .optional()
        .describe('Data de expiração do documento aprovado, quando aplicável'),
})
    .describe('Item do checklist de documentos — um tipo do catálogo e seu estado atual');
export const ChecklistResponseSchema = z
    .object({
    required: z
        .array(DocumentChecklistItemSchema)
        .describe('Tipos de documento exigidos para este fluxo (document_types.required_for)'),
    optional: z
        .array(DocumentChecklistItemSchema)
        .describe('Documentos anexados ao lead fora do rol obrigatório deste fluxo'),
    summary: z.object({
        approved: z.number().int().describe('Quantidade de itens obrigatórios em estado approved'),
        required: z.number().int().describe('Total de itens obrigatórios'),
        complete: z.boolean().describe('true quando todos os itens obrigatórios estão approved'),
    }),
})
    .describe('Checklist de pendências de documentos de um lead para um fluxo (análise/contrato)');
//# sourceMappingURL=documents.js.map